/* import __COLOCATED_TEMPLATE__ from './item.hbs'; */
import { ref } from 'ember-ref-bucket';
import { SELECT_ALL_ITEMS } from 'volta/components/bv-resource-list';
import { idVariation } from 'volta/utils/id';

import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { IBvResourceListContext } from 'volta/components/bv-resource-list';
import type { BvBtnArgs } from 'volta/components/bv-btn';
import type { ComponentLike, ContentValue } from '@glint/template';

import type RouterService from '@ember/routing/router-service';
import type Model from '@ember-data/model';
import type { IBvMenuItem } from 'volta/components/bv-menu';

function getIdentity(item?: { id?: string }) {
  return item && item.id;
}

export type BvResourceListShortcutBtn = BvBtnArgs &
  IBvMenuItem & {
    loading?: boolean;
    content?: string;
    icon?: string;
    destructive?: boolean;
    hidden?: boolean;
    url?: string;
    classNames?: string;
  };

export interface BvResourceListItemSignature {
  Args: {
    disabled?: boolean;
    label?: ContentValue;
    condensed?: boolean;
    isCurrent?: boolean;
    /* Unique identifier for the item */
    itemId?: string | null;
    /* Item index */
    index?: number;
    /* Visually hidden text for screen readers */
    accessibilityLabel?: string;
    /* Id of the element the item onClick controls */
    ariaControls?: string;
    /* Tells screen reader the controlled element is expanded */
    ariaExpanded?: boolean;
    /* Media component or text */
    media?: ContentValue | ComponentLike;
    persistActions?: boolean;
    /* Shortcut actions */
    shortcutActions?: (BvResourceListShortcutBtn | undefined)[];
    /* On click event handler */
    onClick?: (itemId?: string) => void;
    context: IBvResourceListContext;
    item: any;
    selectOnClick?: boolean;
    /* Item link route info */
    href?: string;
    route?: string;
    routeModel?: string | Model;
    routeModels?: string[] | Model[];
    routeQuery?: TQueryParams;
    selected?: boolean;
  };
  Blocks: {
    default: [any, string | undefined, number | undefined];
    media: [any, string | undefined, number | undefined];
  };
  Element: HTMLDivElement;
}

export default class BvResourceListItemComponent extends Component<BvResourceListItemSignature> {
  @service router!: RouterService;

  @ref('containerNode') containerNode?: HTMLElement;
  @ref('buttonOverlay') buttonOverlay?: HTMLElement;

  @tracked focused = false;
  @tracked focusedInner = false;

  elementId = guidFor(this);
  checkboxId = idVariation(this.elementId, 'ResourceListItemCheckbox');
  overlayId = idVariation(this.elementId, 'OverlayId');

  get isSelected() {
    const { context, itemId } = this.args;
    const selectedItems = (context && context.selectedItems) ?? [];

    return (
      selectedItems &&
      ((Array.isArray(selectedItems) && selectedItems.some((i) => getIdentity(i) === itemId)) ||
        selectedItems === SELECT_ALL_ITEMS)
    );
  }

  get linkInfo():
    | { route: any; routeModels: any[]; routeQuery: any }
    | undefined
    | {
        route: string | undefined;
        routeModels: (string | Model)[];
        routeQuery: TQueryParams | undefined;
      } {
    const { href, route, routeModel, routeModels, routeQuery } = this.args;
    try {
      if (href) {
        let routeInfo = this.router.recognize(href);

        if (routeInfo && routeInfo.localName !== 'not-found') {
          if (routeInfo.localName === 'index' && routeInfo.parent) {
            routeInfo = routeInfo.parent;
          }

          const paramRows = routeInfo.paramNames
            .map((paramName) => routeInfo?.params?.[paramName])
            .filter(Boolean);
          return {
            route: routeInfo.name,
            routeModels: paramRows,
            routeQuery: routeInfo.queryParams
          };
        }
        return undefined;
      } else {
        return {
          route,
          routeModels: routeModels?.length ? routeModels : routeModel ? [routeModel] : [],
          routeQuery
        };
      }
    } catch {
      return undefined;
    }
  }

  @action
  handleAnchorFocus() {
    this.focused = true;
    this.focusedInner = true;
  }

  @action
  handleFocusedBlur() {
    this.focused = false;
    this.focusedInner = false;
  }

  @action
  handleFocus(event: FocusEvent) {
    if (
      event.target === this.buttonOverlay ||
      (this.containerNode &&
        event.target === this.containerNode.querySelector(`#${this.overlayId}`))
    ) {
      this.focused = true;
      this.focusedInner = false;
    } else if (this.containerNode && this.containerNode.contains(event.target as Node)) {
      this.focused = true;
      this.focusedInner = true;
    }
  }

  @action
  handleBlur({ relatedTarget }: FocusEvent) {
    if (
      this.containerNode &&
      relatedTarget instanceof Element &&
      this.containerNode.contains(relatedTarget)
    ) {
      return;
    }

    this.focused = false;
    this.focusedInner = false;
  }

  @action
  handleMouseOut() {
    if (this.focused) {
      this.focused = false;
      this.focusedInner = false;
    }
  }

  @action
  handleLargerSelectionArea(event: Event) {
    event.stopPropagation();
    this.handleSelection(!this.isSelected);
  }

  @action
  handleSelection(value: boolean) {
    const { itemId, context, item } = this.args;
    if (!itemId || !context.onSelectionChange) {
      return;
    }
    this.focused = value;
    this.focusedInner = value;
    context.onSelectionChange(value, itemId, item);
  }

  @action
  handleClick(event: MouseEvent) {
    const { href, item, itemId, onClick, context, selectOnClick } = this.args;
    const isLink = Boolean(this.linkInfo?.route ?? href);
    const anchor = this.containerNode && this.containerNode.querySelector('a');

    if (context.selectMode && !selectOnClick) {
      this.handleLargerSelectionArea(event);
      return;
    }

    if (anchor === event.target) {
      return;
    }

    if (selectOnClick) {
      context.onSelectionChange(Boolean(item), itemId, item, selectOnClick);
    } else {
      onClick?.(itemId);
    }

    if (isLink && anchor) {
      anchor.click();
    }
  }

  @action
  handleKeypress(event: KeyboardEvent) {
    const { onClick, context } = this.args;
    const { key } = event;

    if (onClick && key === 'Enter' && !context.selectMode) {
      onClick();
    }
  }

  compareEventNode(event: Event) {
    return this.args.onClick
      ? event.target === this.containerNode
      : (event.target as Element).tagName.toLowerCase() === 'a';
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'BvResourceList::Item': typeof BvResourceListItemComponent;
    'bv-resource-list/item': typeof BvResourceListItemComponent;
  }
}
